/**
 * EU-711 — a decisão de QUANDO o candidato vê "Você está sem internet".
 *
 * Lógica pura: sem DOM, sem React, relógio e agenda injetados. É aqui que mora o
 * risco da feature, então é aqui que a cobertura se concentra — um falso
 * positivo é pior que o bug original (manda o candidato conferir o wi-fi por um
 * `500` nosso, ou tira ele de uma etapa que ia concluir sozinha dois segundos
 * depois).
 *
 * Entradas e efeitos:
 *
 * | Entrada                          | Ação                                    | AC       |
 * |----------------------------------|-----------------------------------------|----------|
 * | sistema reporta perda            | offline IMEDIATO                        | OFF-006  |
 * | `status: 0` com o sistema em pé  | agenda em +CONFIRM, cancelável          | OFF-008  |
 * | `status: 0` de request com teto  | NADA (o teto é dela)                    | OFF-008b |
 * | request em voo ≥ STALL           | arma o teto CEILING daquela request     | OFF-007  |
 * | qualquer resposta (2xx…5xx)      | prova conexão viva                      | OFF-009  |
 * | fim sem resposta e sem ser rede  | só solta a request                      | —        |
 *
 * O teto NÃO aborta nada (FR-012): o `timeoutMs` default do transporte segue
 * intocado. O card pede que a TELA apareça em 15 s, não que a chamada morra em
 * 10 s — baixar o teto do transporte mataria leituras legitimamente lentas.
 */

/**
 * Limite de resposta por tentativa. É o ponto a partir do qual o usuário perde a
 * atenção na tarefa (Nielsen, *Response Times: The 3 Important Limits*): passado
 * isso, um spinner deixa de ser "carregando" e passa a ser "travou".
 */
export const OFFLINE_STALL_MS = 10_000;

/**
 * Teto absoluto entre o início da request e o roteamento. Existe para o caso de
 * conexão PRESENTE sem tráfego (sinal fraco, portal cativo): o navegador não
 * emite `offline`, então sem um teto o candidato ficaria em `loading` para
 * sempre.
 */
export const OFFLINE_ROUTE_CEILING_MS = 15_000;

/**
 * Janela de graça depois de uma falha de rede, quando o sistema ainda diz que há
 * conexão. Os retries transitórios (EU-696 `uploadWithRetry` e `moduleSubmitRetry`,
 * ambos com backoff de 1 s/2 s) REPORTAM a falha na primeira tentativa e só
 * depois vencem — rotear no primeiro sinal quebraria OFF-008 e roubaria da etapa
 * uma conclusão que ia dar certo. Cobre os dois backoffs com margem e o total
 * (falha + janela) cabe folgado no teto acima.
 *
 * DEV-95 (caso A) — a janela foi dimensionada para o BACKOFF entre tentativas
 * (1 s / 2 s), NÃO para a duração de cada tentativa. Requests curtas cabem nessa
 * conta; o upload de currículo não: o corpo de até 30 MB é remontado e reenviado
 * do byte 0 a cada tentativa, então UMA tentativa já leva ~9 s (medido em prd:
 * intervalos de 8,7 s e 10,0 s com um PDF de 25 MB). A janela vencia sempre no
 * meio da sequência e abria "Você está sem internet" com a conexão funcionando —
 * diagnóstico factualmente falso. Por isso ela agora vale só para as requests
 * SEM teto próprio (ver `watchdog` em `reportRequestStart`).
 */
export const OFFLINE_CONFIRM_MS = 5_000;

export interface OfflineDetectorDeps {
  now: () => number;
  schedule: (fn: () => void, delayMs: number) => unknown;
  cancel: (handle: unknown) => void;
  /** Chamado SÓ nas transições de estado, nunca em repetição. */
  onChange: (offline: boolean) => void;
}

export interface OfflineDetector {
  isOffline: () => boolean;
  /** Evento `offline` do navegador / `navigator.onLine === false`. */
  reportSystemOffline: () => void;
  /** Evento `online` do navegador. */
  reportSystemOnline: () => void;
  reportRequestStart: (requestId: number, options?: { watchdog?: boolean }) => void;
  /** A request respondeu — qualquer status. Prova que a conexão está viva. */
  reportRequestSuccess: (requestId: number) => void;
  /**
   * `status: 0` do transporte: queda de conexão ou abort de timeout.
   *
   * `watchdog: false` (o mesmo meta de `reportRequestStart`) isenta a request da
   * janela de confirmação — DEV-95 caso A. O `options` é a fonte primária; o
   * detector também guarda o rastro das isentas em voo, porque quem só tem o
   * `requestId` na mão (um chamador antigo) continua coberto.
   */
  reportNetworkError: (requestId: number, options?: { watchdog?: boolean }) => void;
  /**
   * A request terminou sem responder e SEM ser falha de conexão (o provider de
   * token lançou, por exemplo). Só solta o relógio: não é prova de conexão viva
   * nem evidência de queda.
   */
  reportRequestSettled: (requestId: number) => void;
  /** Fecha a tela ("Tentar novamente"). Reabre se a falha repetir. */
  clear: () => void;
  /** Solta timers pendentes (desmontagem / teardown de teste). */
  dispose: () => void;
}

export function createOfflineDetector(deps: OfflineDetectorDeps): OfflineDetector {
  const { now, schedule, cancel, onChange } = deps;

  let offline = false;
  /** Requests em voo → instante de início. Só as elegíveis ao relógio. */
  const inFlight = new Map<number, number>();
  /**
   * Relógio de CADA request em voo. Por-request de propósito: um sucesso alheio
   * não pode apagar o relógio de uma request que continua pendurada — era assim
   * que um poll de fundo respondendo a cada 10 s deixava um POST travado sem
   * nunca alcançar o teto, prendendo o candidato num spinner eterno.
   */
  const requestClocks = new Map<number, unknown>();
  /**
   * DEV-95 (caso A) — `requestId`s que declararam teto próprio (`watchdog:
   * false`) e estão em voo. O rastro existe porque o `return` antecipado de
   * `reportRequestStart` acontece ANTES de registrar o `requestId`: sem ele, a
   * falha chegaria em `reportNetworkError` indistinguível de uma request comum,
   * e a isenção criada de propósito na ENTRADA seria anulada na SAÍDA.
   */
  const exemptFromConfirm = new Set<number>();
  /** Agendamento da janela de confirmação (global — a última falha manda). */
  let confirmHandle: unknown = null;
  /**
   * Instante da última resposta recebida. É a PROVA de conexão viva: um relógio
   * que vence depois dela sabe que não há o que reportar, sem depender de ter
   * sido cancelado na hora certa.
   */
  let lastResponseAt = Number.NEGATIVE_INFINITY;

  function clearRequestClock(requestId: number): void {
    const handle = requestClocks.get(requestId);
    if (handle === undefined) return;
    cancel(handle);
    requestClocks.delete(requestId);
  }

  function clearConfirm(): void {
    if (confirmHandle === null) return;
    cancel(confirmHandle);
    confirmHandle = null;
  }

  /**
   * DEV-669 — requests que estavam em voo no instante em que a tela abriu. Se uma
   * delas RESPONDER (qualquer status), a tela fecha sozinha: "Você está sem
   * internet" ficou factualmente falso e a resposta segue o seu curso (2xx
   * navega para a tela seguinte; 5xx vai para a tela de erro de servidor da
   * própria superfície). Antes, o candidato ficava na tela de offline com o app
   * já na tela seguinte por baixo, e o "Tentar novamente" parecia não reenviar.
   */
  let offlineTriggers = new Set<number>();

  function goOffline(): void {
    // Idempotente (OFF-011): N sinais → UM roteamento. É o que garante que
    // cliques repetidos no botão de conclusão sem conexão não empilhem entradas
    // de histórico nem disparem a tela duas vezes.
    if (offline) return;
    offline = true;
    offlineTriggers = new Set(inFlight.keys());
    onChange(true);
  }

  return {
    isOffline: () => offline,

    reportSystemOffline() {
      // FR-003 / OFF-006 — o sistema é autoridade sobre a AUSÊNCIA de rede:
      // nada a confirmar, nada a esperar.
      clearConfirm();
      goOffline();
    },

    reportSystemOnline() {
      // Cancela suspeita, mas NÃO fecha a tela já aberta: "voltou" segundo o
      // sistema não prova que a request passa (portal cativo devolve `online`
      // com zero tráfego útil). Quem decide sair é o candidato, pelo botão.
      clearConfirm();
    },

    reportRequestStart(requestId, options) {
      // `watchdog: false` = a request declarou um teto PRÓPRIO (o upload de
      // currículo tem 300 s por EU-558/EU-696). Quem anunciou que demora não é
      // evidência de falta de conexão: sem esta saída, um upload saudável de
      // 40 s abriria "Você está sem internet" no meio do envio.
      if (options?.watchdog === false) {
        exemptFromConfirm.add(requestId);
        return;
      }

      const startedAt = now();
      inFlight.set(requestId, startedAt);

      // FR-004 / OFF-007 — armado no limite por tentativa, dispara no teto: os
      // ~5 s entre um e outro são a folga para os retries transitórios vencerem
      // antes de a tela entrar.
      requestClocks.set(
        requestId,
        schedule(() => {
          requestClocks.delete(requestId);
          if (!inFlight.has(requestId)) return;
          const ceilingIn = startedAt + OFFLINE_ROUTE_CEILING_MS - now();
          requestClocks.set(
            requestId,
            schedule(
              () => {
                requestClocks.delete(requestId);
                // Continua pendurada E nada respondeu desde que ela começou. A
                // segunda metade é o que impede o falso positivo: se qualquer
                // request respondeu nesse meio-tempo, há conexão — e "Você está
                // sem internet" seria factualmente falso.
                if (!inFlight.has(requestId) || lastResponseAt >= startedAt) return;
                goOffline();
              },
              Math.max(0, ceilingIn)
            )
          );
        }, OFFLINE_STALL_MS)
      );
    },

    reportRequestSuccess(requestId) {
      inFlight.delete(requestId);
      exemptFromConfirm.delete(requestId);
      clearRequestClock(requestId);
      // OFF-009 — respondeu, logo há conexão. Vale para QUALQUER status: um 500
      // com internet ativa CANCELA a suspeita em vez de confirmá-la (o candidato
      // tem que ver a tela de erro de servidor, nunca a de sem-internet).
      lastResponseAt = now();
      clearConfirm();
      // DEV-669 — a request que estava pendurada quando a tela abriu respondeu: a
      // tela fecha e a resposta segue. O `online` do sistema continua NÃO fechando
      // (portal cativo); só uma resposta de verdade fecha.
      if (offline && offlineTriggers.has(requestId)) {
        offlineTriggers.clear();
        offline = false;
        onChange(false);
      }
    },

    reportNetworkError(requestId, options) {
      inFlight.delete(requestId);
      const declaredOwnCeiling = options?.watchdog === false || exemptFromConfirm.has(requestId);
      exemptFromConfirm.delete(requestId);
      clearRequestClock(requestId);
      // DEV-95 (caso A) / OFF-008b — a mesma isenção da ENTRADA, agora também na
      // SAÍDA. Uma request que anunciou teto próprio não é evidência de falta de
      // conexão nem quando FALHA: o upload de currículo leva ~9 s por tentativa
      // (corpo de 25 MB remontado do byte 0), então a janela de 5 s sempre
      // venceria no meio do `uploadWithRetry` e o candidato veria "Você está sem
      // internet" navegando normalmente. Quem trata esta falha é a superfície do
      // upload, com a copy que descreve o que houve de fato: "O envio foi
      // interrompido e seu currículo não foi salvo." (EU-558/V67).
      //
      // O que NÃO muda: `navigator.onLine === false` continua abrindo a tela na
      // hora, por `reportSystemOffline` (OFF-006) — o candidato genuinamente sem
      // rede segue coberto.
      if (declaredOwnCeiling) return;
      // FR-009 / OFF-008 — não roteia agora: os retries de EU-696 relatam a
      // falha na 1ª tentativa e só depois vencem. Enquanto a janela corre, o
      // estado exibido é `loading`; uma resposta dentro dela cancela isto.
      if (offline) return;
      clearConfirm();
      confirmHandle = schedule(() => {
        confirmHandle = null;
        goOffline();
      }, OFFLINE_CONFIRM_MS);
    },

    reportRequestSettled(requestId) {
      // Terminou sem responder e sem ser rede — solta o relógio e nada mais. Sem
      // isto o `requestId` ficava preso em voo e o teto abria a tela de offline
      // 15 s depois, com a conexão perfeita.
      inFlight.delete(requestId);
      exemptFromConfirm.delete(requestId);
      clearRequestClock(requestId);
    },

    clear() {
      clearConfirm();
      for (const requestId of [...requestClocks.keys()]) clearRequestClock(requestId);
      inFlight.clear();
      exemptFromConfirm.clear();
      offlineTriggers.clear();
      if (!offline) return;
      offline = false;
      onChange(false);
    },

    dispose() {
      clearConfirm();
      for (const requestId of [...requestClocks.keys()]) clearRequestClock(requestId);
      inFlight.clear();
      exemptFromConfirm.clear();
    },
  };
}
